import React, { useState, useRef, useEffect } from "react";
import { Container, Form, Row, Col, Button } from "react-bootstrap";
import AlertOverlay from "../../../components/Alert";
import { useNavigate } from "react-router-dom";
import { useCargoDocumentType } from "../../../apis/cargoDocumentType/CargoDocumentTypeProvider";
import { getPageName, getUiInterface, getSuccessMessage, getErrorMessage, getWordConnector } from "../../../locales/translationUtils";
import { useTranslation } from 'react-i18next';
import NavBar, { NavBarItem } from "../../../components/NavBar";
import CargoDocumentType from "../../../types/cargoDocumentType";

const CargoDocumentTypeCreateView: React.FC = () => {
    const { t } = useTranslation();
    const [alert, setAlert] = useState<{
        message: string;
        variant: "success" | "danger" | "warning" | "info";
    } | null>(null);
    const { postCargoDocumentType } = useCargoDocumentType();
    const navigate = useNavigate();

    const descriptionRef = useRef<HTMLInputElement | null>(null);

    const [cargoDocumentType, setCargoDocumentType] = useState<CargoDocumentType>({
        id: 0,
        version: 0,
        description: "",
        key: "",
    });

    const navItems: NavBarItem[] = [
        { label: t('cargoDocumentType.labels.home'), route: "/", iconName: "house-door" },
        { label: t('cargoDocumentType.labels.list'), route: "/CargoDocumentType", iconName: "table" },
    ];

    const types = ["", "Text", "Number", "DateTime"];

    const handleChange = (e: React.ChangeEvent<any>) => {
        const { name, value } = e.target;
        setCargoDocumentType((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const nullRequiredFields: string[] = [];

        if (!cargoDocumentType.description) nullRequiredFields.push("Description");
        if (!cargoDocumentType.key) nullRequiredFields.push("Key");

        if (nullRequiredFields.length > 0) {
            const warnMessage: string =
                nullRequiredFields.length === 1
                    ? getUiInterface("fieldRequiredSingle", {
                        field: nullRequiredFields[0],
                    })
                    : getUiInterface("fieldRequiredMultiple", {
                        fields: `${nullRequiredFields.slice(0, -1).join(", ")}${nullRequiredFields.length > 1
                            ? ` ${getWordConnector("and")} `
                            : ""
                            }${nullRequiredFields.slice(-1)}`,
                    });

            setAlert({ message: warnMessage, variant: "danger" });
            return;
        }

        try {
            const response = await postCargoDocumentType(cargoDocumentType);

            if (response?.messages == null && response?.data?.id) {
                const successMessage = getSuccessMessage("createCargoDocumentType", { id: response.data.id });
                navigate(`/CargoDocumentType`, {
                    state: {
                        message: successMessage,
                        variant: "success",
                    },
                });
            } else {
                setAlert({
                    message: getErrorMessage("genericError", { error: response?.messages }),
                    variant: "danger",
                });
            }
        } catch (error) {
            setAlert({
                message: getErrorMessage("genericError", {}),
                variant: "danger",
            });
        }
    };

    useEffect(() => {
        if (descriptionRef.current) {
            descriptionRef.current.focus();
        }
    }, []);

    return (
        <Container fluid>
            <NavBar items={navItems} />
            {alert && (
                <AlertOverlay
                    message={alert.message}
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                />
            )}

            <div className="mb-3"></div>
            <h2>{getPageName("createCargoDocumentType")}</h2>
            <Form onSubmit={handleSubmit}>

                <Row>
                    <Col md={12} className="mb-3">
                        <Form.Group controlId="description">
                            <Form.Label>{t('cargoDocumentType.fields.description')}</Form.Label>
                            <Form.Control
                                ref={descriptionRef}
                                type="text"
                                name="description"
                                value={cargoDocumentType.description}
                                onChange={handleChange}
                                style={{ width: "20%" }}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={12} className="mb-3">
                        <Form.Group controlId="key">
                            <Form.Label>{t('cargoDocumentType.fields.key')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="key"
                                value={cargoDocumentType.key}
                                onChange={handleChange}
                                style={{ width: "20%" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <div className="mt-3 mb-3 d-flex gap-2">
                    <Button variant="primary" onClick={handleSubmit}>
                        {getUiInterface("create")}
                    </Button>
                </div>
            </Form>
        </Container>
    );
};

export default CargoDocumentTypeCreateView;